"use client";
import { resizeImageUrl } from "@lib/util/images";
import type { StoreProductCategory } from "@medusajs/types";
import { clx } from "@medusajs/ui";
import PlaceholderImage from "@modules/common/icons/placeholder-image";
import { buildCategoryTree } from "@modules/layout/templates/nav/category-dropdown";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

function CategoriesBanner({ productCategories }: { productCategories: StoreProductCategory[] }) {
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);

  const categories = buildCategoryTree(productCategories);

  const parentCategory = categories.find((cat) => cat.id === hoveredCategory);

  return (
    <div
      className={`bg-white shadow-lg rounded-lg z-10 min-w-max flex transition-all duration-300 ease-in-out opacity-100 scale-100`}
      onMouseLeave={() => setHoveredCategory(null)}
    >
      <div className="w-[220px] h-[450px] overflow-y-auto">
        {categories.map((category) => (
          <Link
            href={`/categories/${category.handle}`}
            key={category.id}
            className="group"
            onMouseEnter={() => setHoveredCategory(category.id)}
          >
            <div
              className={clx(
                "flex items-center gap-2 px-1 text-[13px] hover:bg-gray-200 rounded-md cursor-pointer font-semibold m-1 transition-all duration-200 hover:text-primary-500",
                {
                  "bg-gray-200": hoveredCategory == category.id,
                }
              )}
            >
              <div className="rounded-md h-[32px] flex items-center">
                {typeof category?.metadata?.thumbnail === "string" ? (
                  <Image
                    src={resizeImageUrl(category.metadata.thumbnail, 22, 22)}
                    alt="Product Thumbnail"
                    width={22}
                    height={22}
                  />
                ) : (
                  <PlaceholderImage size={22} color="gray" />
                )}
              </div>
              {category.name}
            </div>
          </Link>
        ))}
      </div>

      {hoveredCategory !== null && (
        <div
          className={`grid w-fit max-w-[900px] grid-cols-5 gap-5 pl-4 p-3 border-l transition-all duration-300 ease-in-out overflow-y-auto max-h-[600px]`}
        >
          {parentCategory?.category_children?.map((sub) => (
            <div key={sub.id}>
              <Link
                href={`/categories/${sub.handle}`}
                className="py-1 text-[13px] text-[#363636] font-semibold w-[220px] hover:text-primary-500"
              >
                {sub.name}
              </Link>
              <div className="flex flex-col">
                {sub.category_children?.map((child) => (
                  <Link
                    href={`/categories/${child.handle}`}
                    key={child.id}
                    className="text-[13px] text-[#707070] hover:text-primary-500"
                  >
                    {child.name}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CategoriesBanner;
